const cleanPdfText = (value: unknown) =>
  String(value ?? '')
    .replace(/[₹]/g, 'INR ')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const wrapLine = (value: string, max = 88) => {
  const words = value.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > max && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

export type PdfTable = {
  title: string;
  headers: string[];
  rows: Array<Array<string | number>>;
};

export type FinancePdfInput = {
  title: string;
  subtitle: string;
  meta: Array<[string, string]>;
  kpis: Array<{ label: string; value: string; tone?: 'green' | 'red' | 'blue' | 'orange' }>;
  sections: Array<PdfTable>;
  generatedAt: string;
};

const pdfColor = {
  teal: '0.050 0.498 0.451',
  dark: '0.060 0.120 0.190',
  muted: '0.310 0.380 0.480',
  line: '0.850 0.880 0.920',
  soft: '0.950 0.980 0.980',
  green: '0.000 0.520 0.270',
  red: '0.750 0.100 0.120',
  blue: '0.100 0.300 0.900',
  orange: '0.840 0.380 0.000'
};

export function downloadFinancePdf(filename: string, input: FinancePdfInput) {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 36;
  const bottom = 48;
  const contentWidth = pageWidth - margin * 2;
  const pages: string[][] = [[]];
  let y = pageHeight - margin;
  const add = (command: string) => pages[pages.length - 1].push(command);
  const text = (value: unknown, x: number, yy: number, size = 10, font = 'F1', color = pdfColor.dark) => {
    add(`${color} rg BT /${font} ${size} Tf ${x} ${yy} Td (${cleanPdfText(value)}) Tj ET`);
  };
  const rect = (x: number, yy: number, w: number, h: number, color: string, stroke = false) => {
    add(`${color} ${stroke ? 'RG' : 'rg'} ${x} ${yy} ${w} ${h} re ${stroke ? 'S' : 'f'}`);
  };
  const newPage = () => {
    pages.push([]);
    y = pageHeight - margin;
  };
  const ensure = (height: number) => {
    if (y - height < bottom) newPage();
  };

  rect(0, pageHeight - 112, pageWidth, 112, pdfColor.teal);
  text(input.title, margin, pageHeight - 58, 24, 'F2', '1 1 1');
  text(input.subtitle, margin, pageHeight - 80, 10, 'F1', '0.900 1 0.980');
  input.meta.slice(0, 4).forEach(([label, value], index) => {
    text(`${label}: ${value}`, 330, pageHeight - 48 - index * 14, 9, 'F1', '1 1 1');
  });
  y = pageHeight - 140;

  const cardW = (contentWidth - 18) / 4;
  input.kpis.slice(0, 4).forEach((kpi, index) => {
    const x = margin + index * (cardW + 6);
    rect(x, y - 58, cardW, 58, '0.965 0.985 0.985');
    text(kpi.label, x + 8, y - 22, 8, 'F2', pdfColor.muted);
    text(kpi.value, x + 8, y - 43, 13, 'F2', pdfColor[kpi.tone || 'teal']);
  });
  y -= 84;

  const drawTable = (table: PdfTable) => {
    ensure(70);
    text(table.title, margin, y, 14, 'F2', pdfColor.teal);
    y -= 18;
    const colW = contentWidth / Math.max(table.headers.length, 1);
    rect(margin, y - 17, contentWidth, 19, pdfColor.dark);
    table.headers.forEach((header, index) => text(header, margin + index * colW + 4, y - 11, 7, 'F2', '1 1 1'));
    y -= 21;
    const rows = table.rows.length ? table.rows : [['No data']];
    rows.forEach((row, rowIndex) => {
      ensure(24);
      if (rowIndex % 2 === 0) rect(margin, y - 15, contentWidth, 18, '0.980 0.990 0.990');
      row.slice(0, table.headers.length).forEach((cell, index) => {
        const value = String(cell ?? '');
        text(value.length > 26 ? `${value.slice(0, 25)}...` : value, margin + index * colW + 4, y - 10, 7, 'F1', pdfColor.dark);
      });
      rect(margin, y - 15, contentWidth, 18, pdfColor.line, true);
      y -= 18;
    });
    y -= 14;
  };

  input.sections.forEach(drawTable);
  ensure(60);
  text('Notes / Generated by PFS', margin, y, 13, 'F2', pdfColor.teal);
  y -= 18;
  wrapLine('This report is for personal finance tracking and ITR-support preparation. It is not an official tax filing document. Verify with bank and credit card statements before filing.', 100).forEach((line) => {
    text(line, margin, y, 8, 'F1', pdfColor.muted);
    y -= 12;
  });

  pages.forEach((page, index) => {
    page.push(`${pdfColor.line} RG ${margin} 34 ${contentWidth} 0 re S`);
    page.push(`${pdfColor.muted} rg BT /F1 8 Tf ${margin} 24 Td (PFS Finance Report) Tj ET`);
    page.push(`${pdfColor.muted} rg BT /F1 8 Tf ${pageWidth - 155} 24 Td (Page ${index + 1} of ${pages.length} | ${cleanPdfText(input.generatedAt)}) Tj ET`);
  });

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };
  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const boldId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
  const contentIds = pages.map((page) => addObject(`<< /Length ${page.join('\n').length} >>\nstream\n${page.join('\n')}\nendstream`));
  const pageIds = contentIds.map((contentId) => addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldId} 0 R >> >> /Contents ${contentId} 0 R >>`));
  const pagesId = addObject(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
  pageIds.forEach((id) => {
    objects[id - 1] = objects[id - 1].replace('/Parent 0 0 R', `/Parent ${pagesId} 0 R`);
  });
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const blob = new Blob([pdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadTextPdf(filename: string, lines: string[]) {
  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 42;
  const lineHeight = 15;
  const pages: string[][] = [[]];
  let y = pageHeight - margin;

  lines.flatMap((line) => wrapLine(line)).forEach((line) => {
    if (y < margin) {
      pages.push([]);
      y = pageHeight - margin;
    }
    pages[pages.length - 1].push(`BT /F1 10 Tf 42 ${y} Td (${cleanPdfText(line)}) Tj ET`);
    y -= lineHeight;
  });

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const contentIds = pages.map((page) => addObject(`<< /Length ${page.join('\n').length} >>\nstream\n${page.join('\n')}\nendstream`));
  const pageIds = contentIds.map((contentId) => addObject(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`));
  const pagesId = addObject(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
  pageIds.forEach((id) => {
    objects[id - 1] = objects[id - 1].replace('/Parent 0 0 R', `/Parent ${pagesId} 0 R`);
  });
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const blob = new Blob([pdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
